import { PdfTemplateDocumentType } from '../models/entity/pdf-template.entity';

/**
 * PD-14688 — the variable, line-item-column, and totals-row catalogs that
 * drive the PDF template builder for each document type.
 *
 * These catalogs are the single contract between the builder UI (variable
 * picker, column picker), the server-side context builders (which must
 * populate every path listed here), and the renderer. Adding a variable
 * means adding it HERE and populating it in the document type's context
 * builder — the builder UI and preview pick it up automatically.
 *
 * Paths are dotted and resolve through the shared template-utils
 * interpolation ({{client.customerId}} etc.). Sample values feed the
 * builder's live preview so a template reads like a real document while
 * being designed.
 */

/**
 * Date formats a {{token|format}} suffix may request, in the order the
 * builder's variable picker offers them. 'short' is the default a bare
 * token renders with, so the picker inserts it without a suffix. The
 * names map 1:1 onto formatDateSimple's styles.
 */
export const PDF_TEMPLATE_DATE_FORMATS = [
	'short',
	'mdy',
	'medium',
	'long',
	'iso',
] as const;
export type PdfTemplateDateFormat = (typeof PDF_TEMPLATE_DATE_FORMATS)[number];

export interface PdfTemplateVariableDefinitionModel {
	/** Dotted path used inside {{tokens}} — e.g. 'invoice.number'. */
	path: string;
	label: string;
	/** Grouping header in the builder's variable picker. */
	category: string;
	sampleValue: string;
	/**
	 * Marks variables whose context value is a tagged raw date (see
	 * PdfTemplateDateValueModel). The picker expands these into one entry
	 * per PDF_TEMPLATE_DATE_FORMATS format, and the context builders must
	 * emit them via pdfTemplateDateValue() rather than pre-formatting.
	 */
	kind?: 'date';
}

export interface PdfTemplateLineItemColumnDefinitionModel {
	/** Column key — matches the keys of each rendered line-item row. */
	key: string;
	label: string;
	sampleValues: Array<string>;
	align?: 'left' | 'center' | 'right';
	/** Default width hint (percent of the table) used when first added. */
	defaultWidthPct?: number;
}

export interface PdfTemplateTotalsRowDefinitionModel {
	/** Row key — matches the keys of the rendered totals map. */
	key: string;
	label: string;
	sampleValue: string;
	/** Rows that are usually noise at zero default to hidden-when-zero. */
	defaultHideWhenZero?: boolean;
}

export const INVOICE_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Document ────────────────────────────────────────────────────
		{
			path: 'invoice.number',
			label: 'Invoice Number',
			category: 'Invoice',
			sampleValue: 'INV0378415',
		},
		{
			path: 'invoice.date',
			label: 'Invoice Date',
			category: 'Invoice',
			sampleValue: '6/17/2026',
			kind: 'date',
		},
		{
			path: 'invoice.dueDate',
			label: 'Due Date',
			category: 'Invoice',
			sampleValue: '7/10/2026',
			kind: 'date',
		},
		{
			path: 'invoice.paymentTerm',
			label: 'Payment Terms',
			category: 'Invoice',
			sampleValue: 'EOM+10',
		},
		{
			path: 'invoice.status',
			label: 'Invoice Status',
			category: 'Invoice',
			sampleValue: 'Balance Due',
		},
		{
			path: 'invoice.poNumbers',
			label: 'Customer PO #(s)',
			category: 'Invoice',
			sampleValue: 'PO-8543330',
		},
		{
			path: 'invoice.salesOrderNumber',
			label: 'Sales Order #',
			category: 'Invoice',
			sampleValue: 'SO646337596',
		},
		{
			path: 'invoice.orderDate',
			label: 'Order Date',
			category: 'Invoice',
			sampleValue: '6/15/2026',
			kind: 'date',
		},
		{
			path: 'invoice.memo',
			label: 'Memo',
			category: 'Invoice',
			sampleValue: 'Thank you for your business.',
		},
		{
			path: 'invoice.pageTitle',
			label: 'Document Title',
			category: 'Invoice',
			sampleValue: 'Invoice',
		},
		// ── Amounts (also available as totals-box rows) ─────────────────
		{
			path: 'invoice.subtotal',
			label: 'Subtotal',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'invoice.discount',
			label: 'Discount',
			category: 'Amounts',
			sampleValue: '-$12.50',
		},
		{
			path: 'invoice.tax',
			label: 'Tax',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'invoice.shipping',
			label: 'Shipping Cost',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'invoice.processingFee',
			label: 'Processing Fee',
			category: 'Amounts',
			sampleValue: '$6.76',
		},
		{
			path: 'invoice.total',
			label: 'Total',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'invoice.amountPaid',
			label: 'Amount Paid',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'invoice.balanceDue',
			label: 'Balance Due',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		// ── Customer ────────────────────────────────────────────────────
		{
			path: 'client.name',
			label: 'Customer Name',
			category: 'Customer',
			sampleValue: 'Maks Pharma And Diagnostics Inc',
		},
		{
			path: 'client.customerId',
			label: 'Customer ID',
			category: 'Customer',
			sampleValue: '8595',
		},
		{
			path: 'client.email',
			label: 'Customer Email',
			category: 'Customer',
			sampleValue: 'orders@makspharma.com',
		},
		{
			path: 'client.phone',
			label: 'Customer Phone',
			category: 'Customer',
			sampleValue: '(516) 555-0184',
		},
		{
			path: 'client.licenseNumber',
			label: 'Customer License #',
			category: 'Customer',
			sampleValue: '031584',
		},
		{
			path: 'client.deaNumber',
			label: 'Customer DEA #',
			category: 'Customer',
			sampleValue: 'RM0504212',
		},
		{
			path: 'client.salesRep',
			label: 'Sales Rep',
			category: 'Customer',
			sampleValue: 'Dean Sages',
		},
		{
			path: 'client.autoPayMethod',
			label: 'Auto Pay Method',
			category: 'Customer',
			sampleValue: 'Visa ending 4242',
		},
		{
			path: 'client.creditBalance',
			label: 'Credit Balance',
			category: 'Customer',
			sampleValue: '$0.00',
		},
		// ── Shipping ────────────────────────────────────────────────────
		{
			path: 'shipping.method',
			label: 'Shipping Method',
			category: 'Shipping',
			sampleValue: 'UPS Next Day Air',
		},
		{
			path: 'shipping.trackingNumber',
			label: 'Tracking Number',
			category: 'Shipping',
			sampleValue: '1ZF29Y960198627334',
		},
		{
			path: 'shipping.trackingUrl',
			label: 'Tracking URL',
			category: 'Shipping',
			sampleValue: 'https://www.ups.com/track?tracknum=1ZF29Y960198627334',
		},
		// ── Company (the issuing company / tenant) ──────────────────────
		{
			path: 'company.name',
			label: 'Company Name',
			category: 'Company',
			sampleValue: 'Pharmsource, LLC',
		},
		{
			path: 'company.email',
			label: 'Company Email',
			category: 'Company',
			sampleValue: 'ar@pharmsource.com',
		},
	];

export const INVOICE_PDF_TEMPLATE_LINE_ITEM_COLUMNS: Array<PdfTemplateLineItemColumnDefinitionModel> =
	[
		{
			key: 'itemNumber',
			label: 'SKU',
			sampleValues: ['201222', '201471', '205452'],
			defaultWidthPct: 10,
		},
		{
			key: 'description',
			label: 'Description',
			sampleValues: [
				'PANTOPRAZOLE SODIUM DR 40MG TABLET (PROTONIX)',
				'TERCONAZOLE VAGINAL 0.4% CREAM (TERAZOL)',
				'METFORMIN HCL TABLET 500MG (GLUCOPHAGE)',
			],
			defaultWidthPct: 34,
		},
		{
			key: 'size',
			label: 'Size',
			sampleValues: ['1000TB', '45GM', '1X1000EA'],
			defaultWidthPct: 10,
		},
		{
			key: 'unitOfMeasure',
			label: 'UOM',
			sampleValues: ['EA', 'EA', 'EA'],
			defaultWidthPct: 8,
		},
		{
			key: 'ndc',
			label: 'NDC',
			sampleValues: ['31722-0713-10', '51672-1304-06', '69367-0381-10'],
			defaultWidthPct: 14,
		},
		{
			key: 'orderedQuantity',
			label: 'Ordered Qty',
			sampleValues: ['4', '2', '6'],
			align: 'right',
			defaultWidthPct: 8,
		},
		{
			key: 'quantity',
			label: 'Quantity',
			sampleValues: ['4', '2', '6'],
			align: 'right',
			defaultWidthPct: 8,
		},
		{
			key: 'price',
			label: 'Price',
			sampleValues: ['$21.50', '$18.50', '$7.50'],
			align: 'right',
			defaultWidthPct: 8,
		},
		{
			key: 'total',
			label: 'Total',
			sampleValues: ['$86.00', '$37.00', '$45.00'],
			align: 'right',
			defaultWidthPct: 8,
		},
	];

export const INVOICE_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	[
		{ key: 'subtotal', label: 'Subtotal', sampleValue: '$225.40' },
		{
			key: 'discount',
			label: 'Discount',
			sampleValue: '-$12.50',
			defaultHideWhenZero: true,
		},
		{ key: 'tax', label: 'Tax Total', sampleValue: '$0.00' },
		{
			key: 'shipping',
			label: 'Shipping Cost',
			sampleValue: '$0.00',
		},
		{
			key: 'processingFee',
			label: 'Processing Fee',
			sampleValue: '$6.76',
			defaultHideWhenZero: true,
		},
		{ key: 'total', label: 'Total', sampleValue: '$225.40' },
		{
			key: 'amountPaid',
			label: 'Amount Paid',
			sampleValue: '$0.00',
			defaultHideWhenZero: true,
		},
		{
			key: 'balanceDue',
			label: 'Balance Due',
			sampleValue: '$225.40',
		},
	];

/**
 * The customer/company variable groups are identical across document
 * types — built once so a label fix applies everywhere.
 */
const CUSTOMER_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	INVOICE_PDF_TEMPLATE_VARIABLES.filter(
		(variable) => variable.category === 'Customer',
	);

const COMPANY_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	INVOICE_PDF_TEMPLATE_VARIABLES.filter(
		(variable) => variable.category === 'Company',
	);

export const ORDER_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Document ────────────────────────────────────────────────────
		{
			path: 'order.number',
			label: 'Order Number',
			category: 'Order',
			sampleValue: '1268097',
		},
		{
			path: 'order.date',
			label: 'Order Date',
			category: 'Order',
			sampleValue: '6/15/2026',
			kind: 'date',
		},
		{
			path: 'order.customerPo',
			label: 'Customer PO #',
			category: 'Order',
			sampleValue: 'PO-8543330',
		},
		{
			path: 'order.pageTitle',
			label: 'Document Title',
			category: 'Order',
			sampleValue: 'Order',
		},
		// ── Amounts (also available as totals-box rows) ─────────────────
		{
			path: 'order.subtotal',
			label: 'Subtotal',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'order.discount',
			label: 'Discount',
			category: 'Amounts',
			sampleValue: '-$12.50',
		},
		{
			path: 'order.tax',
			label: 'Tax',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'order.shipping',
			label: 'Shipping Cost',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'order.processingFee',
			label: 'Processing Fee',
			category: 'Amounts',
			sampleValue: '$6.76',
		},
		{
			path: 'order.total',
			label: 'Total',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		...CUSTOMER_PDF_TEMPLATE_VARIABLES,
		...COMPANY_PDF_TEMPLATE_VARIABLES,
	];

export const QUOTE_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Document ────────────────────────────────────────────────────
		{
			path: 'quote.number',
			label: 'Quote Number',
			category: 'Quote',
			sampleValue: 'Q-1268097',
		},
		{
			path: 'quote.date',
			label: 'Quote Date',
			category: 'Quote',
			sampleValue: '6/15/2026',
			kind: 'date',
		},
		{
			path: 'quote.customerPo',
			label: 'Customer PO #',
			category: 'Quote',
			sampleValue: 'PO-8543330',
		},
		{
			path: 'quote.pageTitle',
			label: 'Document Title',
			category: 'Quote',
			sampleValue: 'Quote',
		},
		// ── Amounts (also available as totals-box rows) ─────────────────
		{
			path: 'quote.subtotal',
			label: 'Subtotal',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'quote.discount',
			label: 'Discount',
			category: 'Amounts',
			sampleValue: '-$12.50',
		},
		{
			path: 'quote.tax',
			label: 'Tax',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'quote.shipping',
			label: 'Shipping Cost',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'quote.total',
			label: 'Total',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		...CUSTOMER_PDF_TEMPLATE_VARIABLES,
		...COMPANY_PDF_TEMPLATE_VARIABLES,
	];

/**
 * Order/quote line tables show the ORDERED quantity (there is no shipped
 * split before fulfillment), so the ordered-vs-shipped pair collapses to
 * the single Quantity column.
 */
export const ORDER_PDF_TEMPLATE_LINE_ITEM_COLUMNS: Array<PdfTemplateLineItemColumnDefinitionModel> =
	INVOICE_PDF_TEMPLATE_LINE_ITEM_COLUMNS.filter(
		(column) => column.key !== 'orderedQuantity',
	);

export const ORDER_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	[
		{ key: 'subtotal', label: 'Subtotal', sampleValue: '$225.40' },
		{
			key: 'discount',
			label: 'Discount',
			sampleValue: '-$12.50',
			defaultHideWhenZero: true,
		},
		{ key: 'tax', label: 'Tax Total', sampleValue: '$0.00' },
		{
			key: 'shipping',
			label: 'Shipping Cost',
			sampleValue: '$0.00',
			defaultHideWhenZero: true,
		},
		{
			key: 'processingFee',
			label: 'Processing Fee',
			sampleValue: '$6.76',
			defaultHideWhenZero: true,
		},
		{ key: 'total', label: 'Total', sampleValue: '$225.40' },
	];

export const QUOTE_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	ORDER_PDF_TEMPLATE_TOTALS_ROWS.filter((row) => row.key !== 'processingFee');

/**
 * Catalog lookups keyed by document type. Statements are the remaining
 * follow-up; the map shape lets them slot in without touching consumers.
 */
export const PDF_TEMPLATE_VARIABLES_BY_DOCUMENT_TYPE: Partial<
	Record<PdfTemplateDocumentType, Array<PdfTemplateVariableDefinitionModel>>
> = {
	[PdfTemplateDocumentType.INVOICE]: INVOICE_PDF_TEMPLATE_VARIABLES,
	[PdfTemplateDocumentType.ORDER]: ORDER_PDF_TEMPLATE_VARIABLES,
	[PdfTemplateDocumentType.QUOTE]: QUOTE_PDF_TEMPLATE_VARIABLES,
};

export const PDF_TEMPLATE_LINE_ITEM_COLUMNS_BY_DOCUMENT_TYPE: Partial<
	Record<
		PdfTemplateDocumentType,
		Array<PdfTemplateLineItemColumnDefinitionModel>
	>
> = {
	[PdfTemplateDocumentType.INVOICE]: INVOICE_PDF_TEMPLATE_LINE_ITEM_COLUMNS,
	[PdfTemplateDocumentType.ORDER]: ORDER_PDF_TEMPLATE_LINE_ITEM_COLUMNS,
	[PdfTemplateDocumentType.QUOTE]: ORDER_PDF_TEMPLATE_LINE_ITEM_COLUMNS,
};

export const PDF_TEMPLATE_TOTALS_ROWS_BY_DOCUMENT_TYPE: Partial<
	Record<PdfTemplateDocumentType, Array<PdfTemplateTotalsRowDefinitionModel>>
> = {
	[PdfTemplateDocumentType.INVOICE]: INVOICE_PDF_TEMPLATE_TOTALS_ROWS,
	[PdfTemplateDocumentType.ORDER]: ORDER_PDF_TEMPLATE_TOTALS_ROWS,
	[PdfTemplateDocumentType.QUOTE]: QUOTE_PDF_TEMPLATE_TOTALS_ROWS,
};
